import { Get, Post, PostJson } from './helpers';
import { PASSCODE_LIST_TYPE, type EMagicLinkStatus, type MagicLink, type PasscodeListMember } from '../types/working-model';
import type { AudienceMagicLink, AudienceMemberMinimal, MergedAudienceMember } from '@/types/audience-list-members.types';
import { mergedListMemberToAudienceMember, mergedListMemberToPasscodeListMember } from '@/store/utils/audience-members';

export const GetMagicLinksForEvent = async (token: string, channel: number, list_id: number, event_uuid: string): Promise<{
	members: MagicLink[] | AudienceMagicLink[]
}> => {
	return Get((event_uuid ?
		`/v3/admin/events/${channel}/${list_id}/magic-links/${event_uuid}` :
		`/v3/admin/events/${channel}/${list_id}/magic-links`), token, {}, true)
		.then(async res => {
			if (res.ok) {
				const results = await res.json();
				return results;
			}

			throw new Error(`Unable to load magic links from channel - ${channel} audience list - ${list_id} event -${event_uuid}`);
		});
};

export const InvalidateMagicLinksForEvent = async (token: string, channel: number, audience_list: number, event_uuid: string, member_ids?: number[]): Promise<MagicLink[]> => {
	return Post(`/v3/admin/events/magic-links/invalidate`, token, { list_id: audience_list, member_ids, event_uuid }, false, {}, true)
		.then(async res => {
			if (res.ok) {
				const result = await res.json();
				return result;
			}
			throw new Error(`Unable to invalidate magic links from channel - ${channel} audience list - ${audience_list} event -${event_uuid}`);
		});
};

export const InvalidateMagicLinksForEventV2 = async (_: string, channel: number, audience_list: number, event_uuid: string, member_ids?: number[]): Promise<AudienceMagicLink[]> => {
	return PostJson<{
		list_id: number,
		event_uuid: string,
		bl_profiles?: number[],
		channel: number
	}, AudienceMagicLink[]>({
		path: `/v3/admin/events/audience-lists/magic-links/invalidate`,
		data: { channel, list_id: audience_list, bl_profiles: member_ids, event_uuid }
	});
};


export const GenerateMagicLinksForEventV2 = async (channel: number, audience_list: number, event_uuid: string, selectedMembers?: MergedAudienceMember[]) => {
	const audienceMembers = selectedMembers?.map(mergedListMemberToAudienceMember);
	return PostJson<{
		channel: number;
		audience_list: number;
		event_uuid: string;
		passcode_list_type: number;
		selectedMembers?: AudienceMemberMinimal[]
	}, { members: AudienceMagicLink[] }>({
		path: `/v3/admin/events/audience-lists/magic-links/generate`,
		data: {
			channel,
			audience_list,
			event_uuid,
			passcode_list_type: PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION,
			selectedMembers: audienceMembers
		}
	});
};

export const GenerateMagicLinksForEvent = async (token: string, channel: number, audience_list: number, event_uuid: string, passcode_list_type: number, selectedMembers?: MergedAudienceMember[]) => {
	if (selectedMembers?.[0]?.internal_source === 'audience_list_member') {
		return GenerateMagicLinksForEventV2(channel, audience_list, event_uuid, selectedMembers);
	}

	const passcodeListMembers = selectedMembers?.map(mergedListMemberToPasscodeListMember);
	return PostJson<
		{
			channel: number;
			audience_list: number;
			event_uuid: string;
			passcode_list_type: number;
			selectedMembers?: PasscodeListMember[]
		},
		{
			members: {
				event: string;
				status: EMagicLinkStatus;
				magic_link: string;
				passcode_list_member: number;
				passcode_list: number;
				email?: string;
				passcode: string | null;
				created_date: number;
				imported_profile: Record<string, string> | null;
				uses?: number;
				avatar?: string | null;
			}[]
		}
	>({
		path: `/v3/admin/events/magic-links/generate`, data: {
			channel,
			audience_list,
			event_uuid,
			passcode_list_type,
			selectedMembers: passcodeListMembers
		}
	})
		.catch(e => {
			console.error(e);
			throw new Error(`Unable to generate magic links from channel - ${channel} audience list - ${audience_list} event -${event_uuid}`);
		});
};

export const GenerateMagicLinksForList = async (
	token: string,
	channel: number,
	audienceList: number,
	eventReturnFilter?: string,
): Promise<any> => {
	return Post(
		`/v3/admin/channel/${channel}/magic-links/${audienceList}/generate`,
		token,
		{
			event_return_filter: eventReturnFilter,
		},
		false,
		{},
		true,
	)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}
			throw new Error(`Unable to generate magic links from channel - ${channel} audience list - ${audienceList}`);
		});
};

